import type { HubConnection } from '@microsoft/signalr';

import { BaseAPI } from './apis/BaseApi';
import { HttpTransport } from './transport/HttpTransport';
import { LegacyError } from './transport/errors';
import { SignalrTransport } from './transport/SignalrTransport';
import {
  Command,
  CommandResult,
  PushHandler,
  Query,
  QueryResult,
  Transport,
  TransportMode,
  Unsubscribe,
} from './transport/types';

// Re-export the CQRS base types from their canonical home so existing imports
// (`import { Query } from 'src/cqrs/client'`) keep working unchanged.
export type { Query, Command, QueryResult, CommandResult };

/**
 * After a SignalR failure in `auto` mode, stick with HTTP for this long before
 * retrying the hub, so a flapping connection doesn't stall every request on a
 * doomed connect.
 */
const SIGNALR_COOLDOWN_MS = 30_000;

/**
 * OneService — typed client for the One API's generic CQRS endpoints. Sends a
 * `Query` or `Command` and returns its `Result`, over either transport:
 *
 *   - HTTP    : `POST /graph/query`, `PUT /graph/command`
 *   - SignalR : the `graphHubUrl` hub (`invoke('Query'|'Command')`,
 *               `stream('Push')`). This is the shared One hub — the same one the
 *               legacy device events use (`${API_URL}/hub/v1`) — NOT a separate
 *               `/hub/graph` path (which 404s).
 *
 * Transport is chosen per the injected `getTransportMode()` preference:
 *   - `http`    — always HTTP
 *   - `signalr` — always the hub
 *   - `auto`    — prefer the hub, fall back to HTTP on failure (default)
 *
 * The public `query`/`command` signatures are unchanged from the HTTP-only
 * version, so provider APIs and hooks need no changes. `subscribePush` exposes
 * the hub's server-to-client push stream (a no-op under `http`).
 *
 * This covers ONLY the generic graph endpoints. Provider-scoped endpoints
 * (`/Providers/Cozify.<Provider>/<id>/Query|Command`) are handled by the
 * per-provider classes in `./apis`.
 */
export class OneService extends BaseAPI {
  private readonly http: HttpTransport;
  private signalr?: SignalrTransport;
  /** Epoch ms until which `auto` mode skips SignalR after a failure. */
  private signalrDisabledUntil = 0;

  constructor(config: ConstructorParameters<typeof BaseAPI>[0]) {
    super(config);
    this.http = new HttpTransport(config.baseUrl, () => this.getAccessToken());
  }

  private get mode(): TransportMode {
    return this.config.getTransportMode?.() ?? 'auto';
  }

  /** Lazily create the SignalR transport (does not open a connection). */
  private getSignalr(): SignalrTransport {
    if (!this.signalr) {
      // No baseUrl-derived default: the graph hub is the shared One hub
      // (`${API_URL}/hub/v1`), whose path convention `cqrs/client` shouldn't
      // bake in. A wrong guess (the old `${baseUrl}/hub/graph`) silently 404s
      // the negotiate, so require it explicitly and fail loud if it's missing.
      const hubUrl = this.config.graphHubUrl;
      if (!hubUrl) {
        throw new Error(
          'OneService: graphHubUrl is required for the SignalR transport ' +
            '(the app injects `${API_URL}/hub/v1`).'
        );
      }
      this.signalr = new SignalrTransport(
        hubUrl,
        () => this.getAccessToken(),
        () => this.mode !== 'http'
      );
    }
    return this.signalr;
  }

  /**
   * Current live transport state, for a status indicator:
   *   - `connected`  — the SignalR hub is up (green)
   *   - `http`       — using HTTP: either forced, or `auto` fell back (yellow)
   *   - `connecting` — SignalR isn't up yet and we haven't fallen back (red)
   */
  getTransportStatus(): 'connected' | 'connecting' | 'http' {
    const mode = this.mode;
    if (mode === 'http') return 'http';
    if (this.signalr?.isConnected) return 'connected';
    if (mode === 'auto' && Date.now() < this.signalrDisabledUntil)
      return 'http';
    return 'connecting';
  }

  /** Best-effort: bring the SignalR connection up (no-op under `http`). */
  ensureConnected(): void {
    if (this.mode === 'http') return;
    void this.getSignalr()
      .connect()
      .catch(() => {});
  }

  /**
   * True when a live server→client channel is up — i.e. the SignalR socket is
   * connected, so pushes and device events flow. False under `http` (or while a
   * socket is down / degraded), meaning request/response only. UI can gate
   * live-update-dependent controls on this.
   */
  get isBidirectional(): boolean {
    return this.mode !== 'http' && this.signalr?.isConnected === true;
  }

  /** Which SignalR rung is live ('direct' | 'negotiated'), for the status flyout. */
  get transportRung(): 'direct' | 'negotiated' | undefined {
    return this.signalr?.rung;
  }

  /**
   * The shared hub connection for the legacy device-events layer, so it rides
   * the SAME `/hub/v1` connection as the graph CQRS instead of opening a second
   * one. Returns null when the user forced `http` — no socket, so device events
   * are off — or when neither socket rung comes up (degraded).
   */
  async getHubConnection(): Promise<HubConnection | null> {
    if (this.mode === 'http') return null;
    try {
      return await this.getSignalr().getConnection();
    } catch {
      return null;
    }
  }

  /**
   * Register a callback fired when the shared hub reconnects after a drop, so
   * the device-events layer can re-subscribe its server stream (a reconnect
   * silently drops it). No-op under `http`. Returns an unsubscribe.
   */
  onHubReconnected(handler: () => void): Unsubscribe {
    if (this.mode === 'http') return () => {};
    return this.getSignalr().onReconnected(handler);
  }

  /** Pick a transport for a single request, honoring preference + auto fallback. */
  private async resolveTransport(): Promise<Transport> {
    const mode = this.mode;
    if (mode === 'http') return this.http;

    const signalr = this.getSignalr();

    if (mode === 'signalr') {
      await signalr.connect();
      return signalr;
    }

    // auto: prefer SignalR, fall back to HTTP on connect failure.
    if (Date.now() < this.signalrDisabledUntil) return this.http;
    try {
      await signalr.connect();
      return signalr;
    } catch {
      this.signalrDisabledUntil = Date.now() + SIGNALR_COOLDOWN_MS;
      return this.http;
    }
  }

  /** Execute a query (`/graph/query` or hub `Query`). */
  async query<R extends QueryResult = QueryResult>({
    query,
    signal,
  }: {
    query: Query;
    signal?: AbortSignal;
  }): Promise<R> {
    const transport = await this.resolveTransport();
    try {
      return await transport.query<R>(query, signal);
    } catch (e) {
      // In auto mode, a mid-flight SignalR *transport* failure (LegacyError)
      // falls back to HTTP once. A genuine GraphError (E_NOT_FOUND, ...) is not
      // a transport problem — HTTP would just repeat it — so rethrow unchanged.
      if (
        transport.kind === 'signalr' &&
        this.mode === 'auto' &&
        e instanceof LegacyError
      ) {
        this.signalrDisabledUntil = Date.now() + SIGNALR_COOLDOWN_MS;
        return this.http.query<R>(query, signal);
      }
      throw e;
    }
  }

  /** Execute a command (`/graph/command` or hub `Command`). */
  async command<R extends CommandResult = CommandResult>({
    command,
    signal,
  }: {
    command: Command;
    signal?: AbortSignal;
  }): Promise<R> {
    const transport = await this.resolveTransport();
    try {
      return await transport.command<R>(command, signal);
    } catch (e) {
      if (
        transport.kind === 'signalr' &&
        this.mode === 'auto' &&
        e instanceof LegacyError
      ) {
        this.signalrDisabledUntil = Date.now() + SIGNALR_COOLDOWN_MS;
        return this.http.command<R>(command, signal);
      }
      throw e;
    }
  }

  /**
   * Subscribe to server push messages from the Graph hub. Registers the handler
   * on the SignalR transport (created lazily) and, unless the user forced `http`,
   * brings the connection up so pushes start flowing without waiting for the
   * first query. Under `http` this stays dormant until the mode allows SignalR.
   * Returns an unsubscribe.
   */
  subscribePush(handler: PushHandler): Unsubscribe {
    const signalr = this.getSignalr();
    const unsubscribe = signalr.subscribePush(handler);
    if (this.mode !== 'http') {
      void signalr.connect().catch(() => {});
    }
    return unsubscribe;
  }
}
